import React, { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import {
  AutoAwesome as AssistantIcon,
  Computer as ComputerIcon,
  ContentCopy as ContentCopyIcon,
  Block as BlockIcon,
  DeleteOutline as DeleteOutlineIcon,
  Download as DownloadIcon,
  Link as LinkIcon,
  PhoneInTalk as PhoneInTalkIcon,
  SettingsEthernet as SettingsEthernetIcon
} from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { selectCurrentUser } from '../../redux/slices/authSlice';
import {
  canManageTelephony,
  createTelephonyPairingCode,
  deleteTelephonyConnector,
  disableTelephonyConnector,
  setTelephonyConnectorRole,
  subscribeToTelephonyConnectors,
  toDate
} from '../../services/telephonyService';
import { detectTelephonyConnectorDownload } from '../../utils/telephonyConnectorPlatform';

const connectorStatus = (connector) => {
  if (connector?.enabled === false) return { label: 'Deaktiviert', color: 'default' };
  const lastSeenAt = toDate(connector?.lastSeenAt);
  if (!lastSeenAt) return { label: 'Noch nicht verbunden', color: 'warning' };
  if (Date.now() - lastSeenAt.getTime() <= 2 * 60 * 1000) {
    return { label: 'Online', color: 'success' };
  }
  return { label: 'Offline', color: 'error' };
};

const TelephonyConnectorSettingsSection = () => {
  const user = useSelector(selectCurrentUser);
  const [connectors, setConnectors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pairingOpen, setPairingOpen] = useState(false);
  const [displayName, setDisplayName] = useState('Dispositions-PC');
  const [pairing, setPairing] = useState(null);
  const [creatingCode, setCreatingCode] = useState(false);
  const [disablingConnectorId, setDisablingConnectorId] = useState('');
  const [deletingConnectorId, setDeletingConnectorId] = useState('');
  const [updatingRoleConnectorId, setUpdatingRoleConnectorId] = useState('');

  const mayManage = canManageTelephony(user);
  const activeConnectors = useMemo(
    () => connectors.filter((connector) => connector.enabled !== false),
    [connectors]
  );
  const onlineCount = useMemo(
    () => activeConnectors.filter((connector) => connectorStatus(connector).label === 'Online').length,
    [activeConnectors]
  );
  const connectorDownload = useMemo(() => detectTelephonyConnectorDownload(), []);

  useEffect(() => {
    setLoading(true);
    const unsubscribe = subscribeToTelephonyConnectors(user, (items) => {
      setConnectors(items);
      setLoading(false);
      setError('');
    }, (snapshotError) => {
      setLoading(false);
      setError(snapshotError.message || 'Connector-Status konnte nicht geladen werden.');
    });
    return unsubscribe;
  }, [user]);

  const handleCreatePairingCode = async () => {
    setCreatingCode(true);
    setError('');
    try {
      const result = await createTelephonyPairingCode(displayName);
      setPairing(result);
    } catch (callError) {
      setError(callError.message || 'Pairing-Code konnte nicht erzeugt werden.');
    } finally {
      setCreatingCode(false);
    }
  };

  const handleCopyCode = async () => {
    if (pairing?.code) await navigator.clipboard.writeText(pairing.code);
  };

  const closePairing = () => {
    setPairingOpen(false);
    setPairing(null);
  };

  const handleDisable = async (connector) => {
    if (!connector || !window.confirm(`${connector.displayName || connector.hostname || 'Diesen Connector'} wirklich deaktivieren?`)) return;
    setDisablingConnectorId(connector.id);
    setError('');
    try {
      await disableTelephonyConnector(connector.id);
    } catch (callError) {
      setError(callError.message || 'Connector konnte nicht deaktiviert werden.');
    } finally {
      setDisablingConnectorId('');
    }
  };

  const handleRoleChange = async (connector, role) => {
    if (!connector || connector.role === role) return;
    setUpdatingRoleConnectorId(connector.id);
    setError('');
    try {
      await setTelephonyConnectorRole(connector.id, role);
    } catch (callError) {
      setError(callError.message || 'Connector-Rolle konnte nicht aktualisiert werden.');
    } finally {
      setUpdatingRoleConnectorId('');
    }
  };

  const handleDelete = async (connector) => {
    const connectorName = connector?.displayName || connector?.hostname || 'Diesen Connector';
    if (!connector || connector.enabled !== false || !window.confirm(
      `${connectorName} dauerhaft aus dem Portal entfernen? Diese Aktion kann nicht rückgängig gemacht werden.`
    )) return;
    setDeletingConnectorId(connector.id);
    setError('');
    try {
      await deleteTelephonyConnector(connector.id);
    } catch (callError) {
      setError(callError.message || 'Connector konnte nicht entfernt werden.');
    } finally {
      setDeletingConnectorId('');
    }
  };

  return (
    <>
      <Box sx={{ p: 2, bgcolor: 'surface.100' }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <PhoneInTalkIcon color="primary" />
          <Box>
            <Typography variant="subtitle1" fontWeight="bold">
              Telefonie und Anruferkennung
            </Typography>
            <Typography variant="caption" color="text.secondary">
              FRITZ!Box-Connector für eingehende Anrufe
            </Typography>
          </Box>
        </Stack>
      </Box>

      <List disablePadding>
        <ListItem>
          <Stack spacing={2} sx={{ width: '100%' }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="space-between">
              <Stack direction="row" spacing={2} alignItems="center">
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <SettingsEthernetIcon color={connectors.length ? 'primary' : 'action'} />
                </ListItemIcon>
                <ListItemText
                  primary={`${activeConnectors.length} aktive Connectoren`}
                  secondary={connectors.length
                    ? `${onlineCount} online | ${connectors.length - activeConnectors.length} deaktiviert`
                    : 'Noch kein Connector eingerichtet'}
                />
              </Stack>
              {loading && <CircularProgress size={20} />}
            </Stack>

            <Divider />

            <Typography variant="body2" color="text.secondary">
              Mehrere Computer können gleichzeitig mit derselben FRITZ!Box verbunden sein.
              Gleiche Anrufe werden zusammengeführt, sodass im Portal nur ein Hinweis erscheint.
            </Typography>

            {connectors.length > 0 && (
              <Stack spacing={1.5}>
                {connectors.map((connector) => {
                  const itemStatus = connectorStatus(connector);
                  const role = connector.role || 'standard';
                  const isUpdatingRole = updatingRoleConnectorId === connector.id;
                  const isDisabling = disablingConnectorId === connector.id;
                  const isDeleting = deletingConnectorId === connector.id;
                  return (
                    <Box
                      key={connector.id}
                      sx={{ p: 1.5, border: 1, borderColor: 'divider', borderRadius: 1 }}
                    >
                      <Stack
                        direction={{ xs: 'column', md: 'row' }}
                        spacing={1.5}
                        alignItems={{ xs: 'stretch', md: 'center' }}
                        justifyContent="space-between"
                      >
                        <Stack direction="row" spacing={1.5} alignItems="center">
                          <ComputerIcon color={connector.enabled === false ? 'disabled' : 'primary'} />
                          <Box>
                            <Typography variant="subtitle2" fontWeight="bold">
                              {connector.displayName || connector.hostname || 'Unbenannter Connector'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {connector.hostname || 'Unbekannter Computer'} | {connector.platform || 'System unbekannt'} | Version {connector.softwareVersion || 'unbekannt'}
                            </Typography>
                          </Box>
                        </Stack>
                        <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap' }} alignItems="center">
                          <Chip size="small" label={itemStatus.label} color={itemStatus.color} variant="outlined" />
                          <Chip
                            size="small"
                            label={role === 'primary' ? 'Hauptgerät' : role === 'backup' ? 'Ersatzgerät' : 'Standard'}
                            color={role === 'primary' ? 'primary' : role === 'backup' ? 'warning' : 'default'}
                          />
                          {mayManage && connector.enabled !== false && (
                            <>
                              <Button size="small" disabled={isUpdatingRole} onClick={() => handleRoleChange(connector, 'primary')}>
                                Hauptgerät
                              </Button>
                              <Button size="small" disabled={isUpdatingRole} onClick={() => handleRoleChange(connector, 'backup')}>
                                Ersatzgerät
                              </Button>
                              {role !== 'standard' && (
                                <Button size="small" disabled={isUpdatingRole} onClick={() => handleRoleChange(connector, 'standard')}>
                                  Standard
                                </Button>
                              )}
                              <Button
                                size="small"
                                color="error"
                                disabled={isDisabling}
                                startIcon={isDisabling ? <CircularProgress size={16} /> : <BlockIcon />}
                                onClick={() => handleDisable(connector)}
                              >
                                Deaktivieren
                              </Button>
                            </>
                          )}
                          {mayManage && connector.enabled === false && (
                            <Button
                              size="small"
                              color="error"
                              disabled={isDeleting}
                              startIcon={isDeleting ? <CircularProgress size={16} /> : <DeleteOutlineIcon />}
                              onClick={() => handleDelete(connector)}
                            >
                              Entfernen
                            </Button>
                          )}
                        </Stack>
                      </Stack>
                    </Box>
                  );
                })}
              </Stack>
            )}

            <Alert severity="info">
              Ein Hauptgerät wird als bevorzugte Quelle markiert. Ersatzgeräte bleiben
              empfangsbereit und übernehmen automatisch, wenn das Hauptgerät ausfällt.
            </Alert>

            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1}
              useFlexGap
              sx={{ flexWrap: 'wrap' }}
            >
              {connectorDownload ? (
                <Button
                  component="a"
                  href={connectorDownload.href}
                  download
                  variant="contained"
                  startIcon={<DownloadIcon />}
                >
                  {connectorDownload.label}
                </Button>
              ) : (
                <Button variant="contained" disabled startIcon={<DownloadIcon />}>
                  Kein Installer für dieses System
                </Button>
              )}
              <Button
                component={RouterLink}
                to="/einstellungen/telefonie-anleitung"
                variant="outlined"
                startIcon={<AssistantIcon />}
              >
                Assistent
              </Button>
              {mayManage && (
                <Button
                  variant="contained"
                  startIcon={<LinkIcon />}
                  onClick={() => setPairingOpen(true)}
                >
                  Connector koppeln
                </Button>
              )}
            </Stack>

            {!mayManage && (
              <Alert severity="info">
                Nur Administratoren können neue Connectoren koppeln. Status und Anleitung
                bleiben für die Disposition sichtbar.
              </Alert>
            )}
            {connectorDownload?.system && (
              <Typography variant="caption" color="text.secondary">
                Erkanntes System: {connectorDownload.system}
              </Typography>
            )}
            {error && <Alert severity="error">{error}</Alert>}
          </Stack>
        </ListItem>
      </List>

      <Dialog open={pairingOpen} onClose={closePairing} maxWidth="sm" fullWidth>
        <DialogTitle>Telefonie-Connector koppeln</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Alert severity="info">
              {!connectorDownload
                ? 'Die Einrichtung muss auf einem unterstützten Windows-, macOS- oder Linux-Computer durchgeführt werden.'
                : connectorDownload.key === 'windows'
                  ? 'Den Code innerhalb von zehn Minuten im Windows-Setup-Assistenten eingeben.'
                  : connectorDownload.key === 'macos'
                    ? 'Den Code innerhalb von zehn Minuten in der installierten NextCab-App eingeben.'
                  : 'Der Code ist zehn Minuten und nur einmal gültig. Nach der Paketinstallation wird er mit dem unten angezeigten Befehl eingegeben.'}
            </Alert>
            {connectorDownload?.key === 'macos' && (
              <Alert severity="warning">
                Der aktuelle macOS-Testinstaller ist noch nicht von Apple signiert und
                notarisiert. Falls macOS ihn blockiert, versuchen Sie einmal, das Paket zu
                öffnen. Wählen Sie danach unter
                <strong> Systemeinstellungen &gt; Datenschutz &amp; Sicherheit &gt; Dennoch öffnen</strong>
                {' '}und bestätigen Sie mit Mac-Passwort oder Touch ID.
              </Alert>
            )}
            <TextField
              label="Name des Computers"
              value={displayName}
              onChange={(event) => setDisplayName(event.target.value)}
              disabled={Boolean(pairing)}
              fullWidth
            />
            {!pairing ? (
              <Button
                variant="contained"
                onClick={handleCreatePairingCode}
                disabled={creatingCode || !displayName.trim()}
                startIcon={creatingCode ? <CircularProgress size={18} color="inherit" /> : <ComputerIcon />}
              >
                Pairing-Code erzeugen
              </Button>
            ) : (
              <Box sx={{ p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  Pairing-Code
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="h4" fontFamily="monospace" letterSpacing={3}>
                    {pairing.code}
                  </Typography>
                  <Button onClick={handleCopyCode} startIcon={<ContentCopyIcon />}>
                    Kopieren
                  </Button>
                </Stack>
                {!connectorDownload ? (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Einstellungen auf dem Dispositionscomputer öffnen und dort einen
                    neuen Pairing-Code erzeugen.
                  </Typography>
                ) : connectorDownload.key === 'windows' ? (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Diesen Code jetzt in den Windows-Setup-Assistenten übernehmen.
                  </Typography>
                ) : connectorDownload.key === 'macos' ? (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Diesen Code in die Anwendung <strong>NextCab Telefonie-Connector</strong>
                    {' '}eingeben. Die Anwendung wird nach der Paketinstallation automatisch
                    geöffnet und zeigt den Verbindungsstatus auf der letzten Seite.
                  </Typography>
                ) : (
                  <>
                    <Typography variant="caption" color="text.secondary">
                      Nach Installation des Linux-Pakets ausführen:
                    </Typography>
                    <Box component="pre" sx={{ p: 1, bgcolor: 'grey.900', color: 'grey.100', overflowX: 'auto' }}>
                      {`nextcab-telephony-connector setup --code ${pairing.code}`}
                    </Box>
                  </>
                )}
              </Box>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closePairing}>Schließen</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default TelephonyConnectorSettingsSection;
